import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import { createShareLinks } from '../config';
import AppInviteModal from './modals/AppInviteModal';
import AppPreviewModal from './modals/AppPreviewModal';
import PlatformAdminGroupPicker from './modals/PlatformAdminGroupPicker';
import groupService from '../api/groupService';
import {
  Home,
  Users,
  Settings,
  Calendar,
  BookOpen,
  Heart,
  ChevronRight,
  ChevronDown,
  LogOut,
  Crown,
  BarChart3,
  HelpCircle,
  Building2,
  Church,
  Menu,
  X,
  GraduationCap,
  CreditCard,
  Star,
  Book,
  DollarSign,
  Smartphone,
  Share,
  ExternalLink,
  HandHeart,
  FileText
} from 'lucide-react';

const AdminSidebar = ({ isMobile = false, isCollapsed = false, onToggle, onOpenAI }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { adminGroup, adminGroups, isPlatformAdmin, selectGroup } = useGroup();
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [isUpgradeHovered, setIsUpgradeHovered] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isAppPreviewOpen, setIsAppPreviewOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  
  // Use adminGroup instead of currentGroup
  const currentGroup = adminGroup;
  const currentUser = user;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Auto-expand parent menu if on a submenu page
  useEffect(() => {
    if (location.pathname.startsWith('/admin/dashboard/financials/')) {
      setExpandedMenus(prev => ({ 
        ...prev, 
        financials: true
      }));
    }
  }, [location.pathname]);

  // Fetch pending join requests count
  useEffect(() => {
    const fetchPendingRequests = async () => {
      if (adminGroup?.id) {
        try {
          console.log('🔔 Fetching pending requests for group:', adminGroup.id);
          const requests = await groupService.getPendingJoinRequests(adminGroup.id);
          console.log('🔔 Pending requests received:', requests);
          const count = requests?.length || 0;
          console.log('🔔 Setting pending requests count to:', count);
          setPendingRequestsCount(count);
        } catch (error) {
          console.error('❌ Error fetching pending requests in sidebar:', error);
          setPendingRequestsCount(0);
        }
      } else {
        console.log('⚠️ No adminGroup.id available for fetching pending requests');
      }
    };
    
    fetchPendingRequests();
    // Refresh count every 30 seconds while component is mounted
    const interval = setInterval(fetchPendingRequests, 30000);
    return () => clearInterval(interval);
  }, [adminGroup?.id]);

  // Toast notification function
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // Hide after 3 seconds
  };

  // Navigation items - simplified for admin group page
  const navigationItems = [];

  // Settings section
  const settingsItems = [
    {
      id: 'account',
      label: 'Account',
      icon: Settings,
      path: '/admin/dashboard/account',
      isActive: location.pathname === '/admin/dashboard/account'
    }
  ];

  // AI Assistant item - separate from group content
  const aiAssistantItem = {
    id: 'ai-assistant',
    label: 'AI Assistant',
    icon: 'rocket', // Special identifier for RocketIcon
    action: 'openAI', // Special action instead of navigation
    isActive: false // AI Assistant is modal, never technically "active"
  };

  // Group content items - show navigation items even for new groups
  const groupContentItems = [
    {
      id: 'updates',
      label: 'Community Updates',
      icon: Heart,
      path: '/admin/dashboard/updates',
      isActive: location.pathname === '/admin/dashboard/updates'
    },
    {
      id: 'members',
      label: 'Members',
      icon: Users,
      path: '/admin/dashboard/members',
      isActive: location.pathname === '/admin/dashboard/members' || location.pathname.includes('/members/'),
      badge: pendingRequestsCount > 0 ? pendingRequestsCount : null
    },
    {
      id: 'events',
      label: 'Events',
      icon: Calendar,
      path: '/admin/dashboard/events',
      isActive: location.pathname === '/admin/dashboard/events'
    },
    {
      id: 'classes',
      label: 'Classes',
      icon: BookOpen,
      path: '/admin/dashboard/classes',
      isActive: location.pathname === '/admin/dashboard/classes'
    },
    {
      id: 'calendar',
      label: 'Community Calendar',
      icon: Calendar,
      path: '/admin/dashboard/calendar',
      isActive: location.pathname === '/admin/dashboard/calendar'
    },
    {
      id: 'prayer',
      label: 'Service Schedule',
      icon: Book,
      path: '/admin/dashboard/prayer',
      isActive: location.pathname === '/admin/dashboard/prayer'
    },
    {
      id: 'prayer-list',
      label: 'Prayer List',
      icon: HandHeart,
      path: '/admin/dashboard/prayer-list',
      isActive: location.pathname === '/admin/dashboard/prayer-list'
    },
    {
      id: 'financials',
      label: 'Financials',
      icon: DollarSign,
      hasSubmenu: true,
      isActive: location.pathname.startsWith('/admin/dashboard/financials'),
      subItems: [
        {
          id: 'donations',
          label: 'Donations',
          icon: Heart,
          path: '/admin/dashboard/financials/payments',
          isActive: location.pathname === '/admin/dashboard/financials/payments'
        },
        {
          id: 'pledges',
          label: 'Pledges',
          icon: HandHeart,
          path: '/admin/dashboard/financials/pledges',
          isActive: location.pathname === '/admin/dashboard/financials/pledges'
        }
        // Tickets feature temporarily disabled
        // {
        //   id: 'tickets',
        //   label: 'Tickets',
        //   icon: FileText,
        //   path: '/admin/dashboard/financials/tickets',
        //   isActive: location.pathname === '/admin/dashboard/financials/tickets'
        // }
      ]
    }
  ];

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleNavigation = (path, disabled = false, action = null) => {
    if (disabled) return;
    
    if (action === 'openAI' && onOpenAI) {
      onOpenAI();
    } else if (path) {
      navigate(path);
    }
  };

  const handleLogout = () => {
    // Immediately clear all auth data and redirect
    localStorage.clear();
    window.location.replace('/login');
  };

  // Mobile toggle handler
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Render mobile toggle button (for use in parent components)
  const MobileToggleButton = () => (
    <button
      onClick={toggleMobileMenu}
      className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200"
    >
      {isMobileMenuOpen ? (
        <X className="w-6 h-6 text-gray-600" />
      ) : (
        <Menu className="w-6 h-6 text-gray-600" />
      )}
    </button>
  );

  return (
    <>
      {/* Mobile Toggle Button */}
      <MobileToggleButton />

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
      `}>
        {/* Header */}
        <div className={`border-b border-gray-200 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2.5'}`}>
            {adminGroup && adminGroup.logo_url ? (
              <div className={`${isCollapsed ? 'w-9 h-9' : 'w-10 h-10'} rounded-lg overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0`}>
                <img 
                  src={adminGroup.logo_url} 
                  alt={`${adminGroup.name} logo`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to default icon if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className={`${isCollapsed ? 'w-9 h-9' : 'w-10 h-10'} bg-gray-900 rounded-lg items-center justify-center hidden`}>
                  <Home className={`${isCollapsed ? 'w-4 h-4' : 'w-5 h-5'} text-white`} />
                </div>
              </div>
            ) : (
              <div className={`${isCollapsed ? 'w-9 h-9' : 'w-10 h-10'} bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Home className={`${isCollapsed ? 'w-4 h-4' : 'w-5 h-5'} text-white`} />
              </div>
            )}
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="text-base font-semibold text-gray-900 leading-tight break-words">
                  {adminGroup ? adminGroup.name : 'Dashboard'}
                </h1>
                <p className="text-xs text-gray-500">
                  {adminGroup ? 'Admin Panel' : 'v.01'}
                </p>
                {isPlatformAdmin && adminGroup && (
                  <button
                    onClick={() => setShowGroupPicker(true)}
                    className="text-xs text-indigo-500 hover:text-indigo-700 font-medium mt-0.5 underline-offset-2 hover:underline"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    👑 Switch community
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Platform admin group picker modal */}
        {showGroupPicker && isPlatformAdmin && (
          <PlatformAdminGroupPicker
            groups={adminGroups}
            onSelect={(group) => {
              selectGroup(group);
              setShowGroupPicker(false);
            }}
            onClose={() => setShowGroupPicker(false)}
          />
        )}

        {/* Navigation */}
        <div className={`flex-1 overflow-y-auto ${isCollapsed ? 'p-2' : 'p-3'} space-y-1`}>
          {/* Preview App Button - Top of sidebar */}
          {adminGroup?.id && (
            <button
              onClick={() => {
                setIsAppPreviewOpen(true);
                // Close mobile menu when opening preview
                setIsMobileMenuOpen(false);
              }}
              className={`
                w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors
                text-gray-700 hover:bg-gray-100
              `}
              title={isCollapsed ? 'Preview app' : ''}
            >
              <div className="flex items-center space-x-2.5">
                <Smartphone className="w-4 h-4" />
                {!isCollapsed && <span className="text-sm font-medium">Preview app</span>}
              </div>
              {!isCollapsed && <ExternalLink className="w-3.5 h-3.5 text-gray-400" />}
            </button>
          )}

          {/* Share App Button - Below View App */}
          {adminGroup?.id && !isCollapsed && (
            <button
              onClick={() => setIsDownloadModalOpen(true)}
              className={`
                w-full flex items-center px-2.5 py-2 rounded-lg text-left transition-colors
                text-gray-700 hover:bg-gray-100 mb-3
                ${isCollapsed ? 'justify-center' : 'space-x-2.5'}
              `}
              title={isCollapsed ? 'Share app' : ''}
            >
              <Share className="w-4 h-4" />
              {!isCollapsed && <span className="text-sm font-medium">Share app</span>}
            </button>
          )}
          
          {/* Main Navigation */}
          <div className="space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path, item.disabled)}
                  disabled={item.disabled}
                  className={`
                    w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors text-sm
                    ${item.isActive 
                      ? 'bg-gray-100 text-gray-900' 
                      : item.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                  title={isCollapsed ? item.label : ''}
                >
                  <div className={`flex items-center ${isCollapsed ? '' : 'space-x-2.5'}`}>
                    <Icon className="w-4 h-4" />
                    {!isCollapsed && <span className="font-medium">{item.label}</span>}
                  </div>
                  {!item.isActive && !item.disabled && !isCollapsed && (
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* AI Assistant Section */}
          {!isCollapsed && (
            <button
              onClick={() => handleNavigation(aiAssistantItem.path, aiAssistantItem.disabled, aiAssistantItem.action)}
              disabled={aiAssistantItem.disabled}
              className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors text-gray-700 hover:bg-gray-100 mb-3"
            >
              <div className="flex items-center space-x-2">
                <img 
                  src="/assets/RocketIcon.png" 
                  alt="AI Assistant" 
                  className="w-4 h-4"
                />
                <span className="font-medium text-sm">{aiAssistantItem.label}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-medium text-blue-600 bg-blue-100 rounded-full">
                  BETA
                </span>
              </div>
            </button>
          )}

          {/* AI Assistant - Collapsed */}
          {isCollapsed && (
            <div className="space-y-1">
              <button
                onClick={() => handleNavigation(aiAssistantItem.path, aiAssistantItem.disabled, aiAssistantItem.action)}
                disabled={aiAssistantItem.disabled}
                className="w-full flex items-center justify-center px-2.5 py-2 rounded-lg transition-colors hover:opacity-90"
                style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}
                title={aiAssistantItem.label}
              >
                <div className="relative">
                  <img 
                    src="/assets/RocketIcon.png" 
                    alt="AI Assistant" 
                    className="w-4 h-4"
                  />
                  <span className="absolute -top-0.5 -right-0.5 px-0.5 py-0.5 text-xs font-bold text-blue-600 bg-blue-100 rounded-full text-[8px]">
                    β
                  </span>
                </div>
              </button>
            </div>
          )}

          {/* Group Content Section */}
          {groupContentItems.length > 0 && !isCollapsed && (
            <div className="pt-6">
              <div className="px-2.5 mb-2">
                <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  My Community
                </h3>
              </div>
              <div className="space-y-1">
                {groupContentItems.map((item) => {
                  const Icon = item.icon;
                  const isExpanded = expandedMenus[item.id];
                  
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => item.hasSubmenu ? toggleMenu(item.id) : handleNavigation(item.path, item.disabled, item.action)}
                        disabled={item.disabled}
                        className={`
                          w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors text-sm
                          ${!item.hasSubmenu && item.isActive 
                            ? 'bg-gray-100 text-gray-900' 
                            : item.disabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-2.5">
                          <Icon className="w-4 h-4" />
                          <span className="font-medium">{item.label}</span>
                          {item.badge && (
                            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full">
                              {item.badge}
                            </span>
                          )}
                          {item.comingSoon && (
                            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-600 rounded-full">
                              Coming Soon
                            </span>
                          )}
                          {item.isNew && (
                            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded-full">
                              New
                            </span>
                          )}
                        </div>
                        {item.hasSubmenu && (
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </button>
                      
                      {/* Submenu items */}
                      {item.hasSubmenu && isExpanded && item.subItems && (
                        <div className="mt-1 space-y-1">
                          {item.subItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => handleNavigation(subItem.path)}
                                className={`
                                  w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-lg text-left transition-colors text-sm
                                  ${subItem.isActive 
                                    ? 'bg-gray-100 text-gray-900 font-medium' 
                                    : 'text-gray-700 hover:bg-gray-100 font-medium'
                                  }
                                `}
                              >
                                {SubIcon && <SubIcon className="w-4 h-4" />}
                                <span>{subItem.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Collapsed Group Content Icons */}
          {groupContentItems.length > 0 && isCollapsed && (
            <div className="pt-6 space-y-1">
              {groupContentItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path, item.disabled, item.action)}
                    disabled={item.disabled}
                    title={item.comingSoon ? `${item.label} - Coming Soon` : item.isNew ? `${item.label} - New` : item.label}
                    className={`
                      w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors relative
                      ${item.isActive 
                        ? 'bg-gray-100 text-gray-900' 
                        : item.disabled
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {item.badge && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {item.comingSoon && !item.badge && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full"></div>
                    )}
                    {item.isNew && !item.badge && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Settings Section */}
          {!isCollapsed && (
            <div className="pt-6">
              <div className="px-2.5 mb-2">
                <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  Settings
                </h3>
              </div>
              <div className="space-y-1">
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path, item.disabled)}
                      disabled={item.disabled}
                      className={`
                        w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors text-sm
                        ${item.isActive 
                          ? 'bg-gray-100 text-gray-900' 
                          : item.disabled
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-2.5">
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Collapsed Settings Icons */}
          {isCollapsed && (
            <div className="pt-6 space-y-1">
              {settingsItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path, item.disabled)}
                    disabled={item.disabled}
                    title={item.label}
                    className={`
                      w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors
                      ${item.isActive 
                        ? 'bg-gray-100 text-gray-900' 
                        : item.disabled
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Logout Button */}
        <div className={`border-t border-gray-200 ${isCollapsed ? 'p-2' : 'p-3'}`}>
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center px-2.5 py-2 rounded-lg text-left transition-colors
              text-gray-700 hover:bg-gray-100
              ${isCollapsed ? 'justify-center' : 'space-x-2.5'}
            `}
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>

        {/* Desktop Collapse Toggle */}
        {onToggle && (
          <button
            onClick={onToggle}
            className="hidden lg:block absolute -right-3 top-6 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
          >
            <ChevronRight className={`w-4 h-4 text-gray-600 transition-transform ${isCollapsed ? 'rotate-0' : 'rotate-180'}`} />
          </button>
        )}
      </div>

      {/* App Invite Modal */}
      <AppInviteModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        groupId={currentGroup?.id || adminGroup?.id}
        groupName={currentGroup?.name || adminGroup?.name}
      />

      {/* App Preview Modal */}
      <AppPreviewModal
        isOpen={isAppPreviewOpen}
        onClose={() => setIsAppPreviewOpen(false)}
        groupId={adminGroup?.id}
      />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-in fade-in duration-300">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{toastMessage}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
